import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
  ConfigProvider,
  App as AntApp,
  Typography,
  Tabs,
  Button,
  Flex,
  Input,
  Modal,
  Alert,
  Avatar,
  Spin,
} from 'antd';
import {
  AppstoreOutlined,
  DashboardOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  FileTextOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Card as SRSCard,
  CardNoteSide,
  Profile,
  UserKnownWord,
  isLocked,
  isRetired,
  showsTrial,
  translationFor,
} from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import { useSenseChooser, wordWithHint } from '../components/SenseChooser.js';
import {
  CardCreationError,
  createChosenCards,
  upgradeLegacyCards,
} from '../services/wordCardService.js';
import { ensureCardImage } from '../services/cardImageService.js';
import { saveCardNote } from '../services/cardNotes.js';
import { unretireCardsFor } from '../services/knownCards.js';
import { runTranslationBackfill } from '../services/translationService.js';
import { baseTranslationLanguage } from '../services/translationSettings.js';
import { antThemeConfig } from '../theme/index.js';
import { getCurrentUser, isOnboardingCompleteAnywhere } from '../auth/index.js';
import { getSyncEngine } from '../services/syncEngine.js';
import { AuthGate } from '../components/AuthGate.js';
import { confirmAndSignOut } from '../components/confirmSignOut.js';
import { DashboardTab } from '../components/DashboardTab.js';
import { EchoLogo } from '../components/Echo.js';
import { DecksTab } from '../components/DecksTab.js';
import { OnboardingView } from '../components/OnboardingView.js';
import {
  BillingFooter,
  LockScreen,
  SubscribePage,
  TrialLine,
  useAccess,
} from '../components/billing/index.js';
import { CardsTab } from './CardsTab.js';
import { KnownWordsTab } from './KnownWordsTab.js';
import { SettingsTab } from './SettingsTab.js';
import { DashboardTour } from './DashboardTour.js';
import { tourTarget } from './tourTargets.js';
import { type OptionsTab, hashForTab, tabFromHash } from './tabs.js';
import { platform } from '../platform.js';
import { User } from '@supabase/supabase-js';

const { Title, Text } = Typography;

const PAGE_TITLE = 'LingoPing Dashboard';

export const OptionsAppInner: React.FC = () => {
  const { message, modal } = AntApp.useApp();

  // The active tab lives in the URL hash (`/#decks`) so a reload stays on it.
  const [activeTab, setActiveTab] = useState<OptionsTab>(() => tabFromHash(window.location.hash));
  const [profile, setProfile] = useState<Profile | null>(null);
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [cards, setCards] = useState<SRSCard[]>([]);
  const [knownWords, setKnownWords] = useState<UserKnownWord[]>([]);
  const [needsOnboarding, setNeedsOnboarding] = useState<boolean | null>(null);
  const onboardingRunning = useRef(false);
  const [loadFailed, setLoadFailed] = useState(false);
  // Trial, subscription, locked: the server's answer. Nothing below changes unless payments are on.
  const { access, refresh: refreshAccess } = useAccess();
  const enforced = !!access?.enforced;
  const locked = isLocked(access);
  /** Bumped on every reload so tabs that load their own data (the dashboard) refresh too. */
  const [dataVersion, setDataVersion] = useState(0);

  const selectTab = (tab: OptionsTab) => {
    setActiveTab(tab);
    // replaceState: switching tabs doesn't add history entries, so Back leaves the page.
    if (window.location.hash !== hashForTab(tab)) {
      history.replaceState(history.state, '', hashForTab(tab));
    }
  };

  // Follow hash changes (a deep link into an already-open page, or an edited URL).
  useEffect(() => {
    const onHashChange = () => setActiveTab(tabFromHash(window.location.hash));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Add-word modal
  const [tourOpen, setTourOpen] = useState(false);
  const [addWordOpen, setAddWordOpen] = useState(false);
  const [addWordText, setAddWordText] = useState('');
  const [addingWord, setAddingWord] = useState(false);
  const { ask: askSense, chooser: senseChooser } = useSenseChooser();

  // Load all repository data
  const loadAllData = async () => {
    try {
      const currentUser = await getCurrentUser();
      // First: on a device that has not heard from the account yet this waits for the server,
      // and the cards and known words read below are then the account's.
      const onboardingDone = await isOnboardingCompleteAnywhere();
      const [prof, allCards, allKnown] = await Promise.all([
        StorageManager.getProfile(),
        StorageManager.getCards(),
        StorageManager.getKnownWords(),
      ]);

      setProfile(prof);
      setCards(allCards);
      setKnownWords(allKnown);
      setAuthUser(currentUser);
      // Once the first-run flow is showing it stays until the learner leaves it: their own choices
      // (the first cards, the finished flag) would otherwise switch it off under their feet.
      if (!onboardingDone && allCards.length === 0 && allKnown.length === 0) {
        onboardingRunning.current = true;
      }
      setNeedsOnboarding(onboardingRunning.current);
      setDataVersion((v) => v + 1);
      setLoadFailed(false);
    } catch (err) {
      console.error('Failed to load options data:', err);
      setLoadFailed(true);
    }
  };
  // Always call the latest closure from long-lived listeners.
  const loadAllDataRef = useRef(loadAllData);
  useLayoutEffect(() => {
    loadAllDataRef.current = loadAllData;
  });

  useEffect(() => {
    document.title = PAGE_TITLE;
    void loadAllDataRef.current();
    // Older cards (placeholder examples from before AI generation) get fresh text and a picture.
    // Then every card is given the translations it is missing (all languages), in the background.
    void upgradeLegacyCards((updated) => void ensureCardImage(updated))
      .then((count) => {
        if (count > 0) void loadAllDataRef.current();
      })
      .then(() => runTranslationBackfill())
      .then((result) => {
        if (result.translated > 0) void loadAllDataRef.current();
      });

    // Reviews and new cards happen in other windows (popup, review window): refresh when the user
    // comes back to this page so the numbers are never stale.
    const onFocus = () => void loadAllDataRef.current();
    window.addEventListener('focus', onFocus);
    // The same when a sync brought something from the learner's other devices.
    const offSync = getSyncEngine().onCacheChanged(onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      offSync();
    };
  }, []);

  const handleAddWord = async () => {
    const word = addWordText.trim();
    if (!word) return;

    try {
      setAddingWord(true);
      const targetLang = profile?.target_language || 'de';

      const newCards = await createChosenCards(
        word,
        profile?.id || 'local-user-1',
        targetLang,
        askSense
      );
      // The learner closed the question about the word's meanings: nothing is added.
      if (newCards.length === 0) return;

      for (const newCard of newCards) {
        await StorageManager.addCard(newCard);
        void ensureCardImage(newCard);
      }
      const newCard = newCards[0];
      if (newCards.length > 1) {
        message.success(`Added ${newCards.length} cards for “${newCard.front_text}”.`);
      } else if (newCard.content_status === 'pending') {
        // The card couldn't be prepared just now: the word waits in Up next as "Preparing…".
        message.info(`Saved “${newCard.front_text}”. Its card will be prepared in the background.`);
      } else {
        const meaning = translationFor(newCard, baseTranslationLanguage(profile)).text;
        message.success(
          `Added “${wordWithHint(newCard.front_text, newCard.sense_hint)}” (${meaning}) to your list of words to learn.`
        );
      }
      setAddWordText('');
      setAddWordOpen(false);
      await loadAllData();
    } catch (err) {
      console.error('Adding a word failed:', err);
      message.error(
        err instanceof CardCreationError
          ? err.message
          : "That word couldn't be added. Please try again."
      );
    } finally {
      setAddingWord(false);
    }
  };

  const handleAddKnownWord = async (word: string) => {
    await StorageManager.addKnownWord(word, profile?.target_language || 'de');
    message.success(`Added “${word}” to your known words.`);
    await loadAllData();
  };

  const handleRemoveKnownGroup = async (base: string) => {
    await StorageManager.removeKnownGroup(base);
    // A card the learner retired for this word ("I already know this word") is shown again.
    await unretireCardsFor(base);
    message.success(`Removed “${base}” from your known words.`);
    await loadAllData();
  };

  const handleDeleteCard = async (cardId: string) => {
    await StorageManager.deleteCard(cardId);
    message.success('Card deleted.');
    await loadAllData();
  };

  const handleSaveNote = async (cardId: string, side: CardNoteSide, text: string) => {
    try {
      await saveCardNote(cardId, side, text);
      await loadAllData();
    } catch (err) {
      console.error('Could not save the note:', err);
      message.error("Your note couldn't be saved. Please try again.");
      throw err;
    }
  };

  const handleSignOut = async () => {
    if (!(await confirmAndSignOut(modal))) return;
    setAuthUser(null);
    message.info('You have been signed out.');
  };

  const displayName = authUser?.user_metadata?.full_name || authUser?.email;
  const showTabs = needsOnboarding === false && profile !== null && !locked;
  // The Subscribe page (`#subscribe`) only exists once payments are on.
  const subscribeOpen = enforced && activeTab === 'subscribe';
  const tabsTab: OptionsTab = activeTab === 'subscribe' ? 'dashboard' : activeTab;
  const { ReviewAction, Notice } = platform;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f8fafc',
        fontFamily: antThemeConfig.token?.fontFamily,
      }}
    >
      {/* Top bar */}
      <header
        style={{
          background: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          padding: '12px 24px',
        }}
      >
        <Flex
          justify="space-between"
          align="center"
          wrap
          gap={12}
          style={{ maxWidth: 1200, margin: '0 auto' }}
        >
          <Flex align="center" gap={12}>
            <EchoLogo size={36} />
            <Flex vertical style={{ lineHeight: 1.25 }}>
              <Title level={4} style={{ margin: 0 }}>
                LingoPing
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Your German vocabulary
              </Text>
            </Flex>
          </Flex>

          <Flex align="center" gap={16} wrap>
            {access && showsTrial(access) && <TrialLine access={access} />}
            {showTabs && ReviewAction && (
              <span {...tourTarget('review')}>
                <ReviewAction />
              </span>
            )}
            {showTabs && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setAddWordText('');
                  setAddWordOpen(true);
                }}
                {...tourTarget('add-word')}
              >
                Add word
              </Button>
            )}
            {showTabs && (
              <Button
                type="text"
                icon={<QuestionCircleOutlined />}
                onClick={() => setTourOpen(true)}
              >
                Tour
              </Button>
            )}
            {authUser && (
              <Flex align="center" gap={8} style={{ minWidth: 0 }}>
                <Avatar
                  size="small"
                  src={authUser.user_metadata?.avatar_url}
                  icon={<UserOutlined />}
                />
                <Text ellipsis style={{ maxWidth: 220 }} title={displayName}>
                  {displayName}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </header>

      {Notice && <Notice />}

      {/* Main content */}
      <main
        style={{
          padding: 24,
          maxWidth: 1200,
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {subscribeOpen ? (
          <SubscribePage
            access={access}
            refreshAccess={refreshAccess}
            onBack={() => selectTab('dashboard')}
          />
        ) : locked && access ? (
          <LockScreen reason={access.reason} onSignOut={handleSignOut} />
        ) : loadFailed && !profile ? (
          <Alert
            type="error"
            showIcon
            title="We couldn't load your data."
            description="Please try again. If this keeps happening, reload the page."
            action={
              <Button size="small" onClick={() => void loadAllData()}>
                Try again
              </Button>
            }
          />
        ) : needsOnboarding === null || profile === null ? (
          <Flex justify="center" align="center" style={{ minHeight: 300 }}>
            <Spin size="large" aria-label="Loading your dashboard" />
          </Flex>
        ) : needsOnboarding ? (
          <OnboardingView
            userId={profile.id || 'local-user-1'}
            trialNotice={showsTrial(access)}
            onComplete={async () => {
              onboardingRunning.current = false;
              await loadAllData();
              // A new learner's first look at the dashboard: show them around once.
              setTourOpen(true);
            }}
          />
        ) : (
          <Tabs
            activeKey={tabsTab}
            onChange={(key) => {
              selectTab(tabFromHash(key));
              // Cards and known words can change while another tab (a swipe session) was open.
              void loadAllData();
            }}
            type="card"
            items={[
              {
                key: 'dashboard',
                label: (
                  <span>
                    <DashboardOutlined /> Dashboard
                  </span>
                ),
                children: (
                  <DashboardTab refreshKey={dataVersion} onGoToDecks={() => selectTab('decks')} />
                ),
              },
              {
                key: 'decks',
                label: (
                  <span>
                    <AppstoreOutlined /> Decks
                  </span>
                ),
                children: (
                  <DecksTab
                    userId={profile.id || 'local-user-1'}
                    isActive={activeTab === 'decks'}
                    onChanged={loadAllData}
                  />
                ),
              },
              {
                key: 'known',
                label: (
                  <span>
                    <CheckCircleOutlined /> Known words
                  </span>
                ),
                children: (
                  <KnownWordsTab
                    knownWords={knownWords}
                    onAdd={handleAddKnownWord}
                    onRemoveGroup={handleRemoveKnownGroup}
                  />
                ),
              },
              {
                key: 'cards',
                label: (
                  <span>
                    <FileTextOutlined /> Cards
                  </span>
                ),
                children: (
                  <CardsTab
                    cards={cards.filter((c) => !isRetired(c) && c.content_status !== 'pending')}
                    translationLanguage={baseTranslationLanguage(profile)}
                    onDelete={handleDeleteCard}
                    onSaveNote={handleSaveNote}
                  />
                ),
              },
              {
                key: 'settings',
                label: (
                  <span>
                    <SettingOutlined /> Settings
                  </span>
                ),
                children: (
                  <SettingsTab
                    profile={profile}
                    authUser={authUser}
                    onSaved={setProfile}
                    onSignOut={handleSignOut}
                    access={access}
                    refreshAccess={refreshAccess}
                  />
                ),
              },
            ]}
          />
        )}
      </main>

      {enforced && <BillingFooter />}

      {/* Add word modal */}
      <DashboardTour
        open={tourOpen && showTabs}
        onClose={() => setTourOpen(false)}
        hasReviewButton={!!ReviewAction}
        onShowTab={selectTab}
      />

      {senseChooser}
      <Modal
        title="Add a word"
        open={addWordOpen}
        onCancel={() => setAddWordOpen(false)}
        onOk={handleAddWord}
        confirmLoading={addingWord}
        okText="Add card"
        cancelText="Cancel"
        okButtonProps={{ disabled: !addWordText.trim() }}
        destroyOnHidden
      >
        <Flex vertical gap={12} style={{ padding: '8px 0' }}>
          <Text type="secondary">Type a German word or phrase. LingoPing makes the card.</Text>
          <Input
            placeholder="For example: der Tisch, Fernweh, gemütlich"
            aria-label="German word or phrase"
            value={addWordText}
            onChange={(e) => setAddWordText(e.target.value)}
            onPressEnter={handleAddWord}
            autoFocus
          />
        </Flex>
      </Modal>
    </div>
  );
};

export const OptionsApp: React.FC = () => {
  return (
    <ConfigProvider theme={antThemeConfig}>
      <AntApp>
        <AuthGate
          showUserBar={false}
          viewTitle="Dashboard"
          containerStyle={{ minHeight: '100vh', backgroundColor: '#f1f5f9' }}
        >
          <OptionsAppInner />
        </AuthGate>
      </AntApp>
    </ConfigProvider>
  );
};
